<template>
    <div :id="tab">
        <v-card :disabled="isLoading" class="v-application appointments-overview">
            <v-card-title>
                <v-text-field
                    v-model="search"
                    v-if="!isPharmacy"
                    append-icon="mdi-magnify"
                    placeholder="Suche nach Datum, Name der Apotheke"
                    class="search-appointments"
                    single-line
                    hide-details
                    clearable
                    autofocus
                ></v-text-field>
            </v-card-title>
            <v-data-table
                :headers="headers"
                :items="tableRows"
                class="v-application--is-ltr"
                mobile-breakpoint="670"
                :items-per-page="25"
                :page.sync="pageNumber"
                :options.sync="tableOptions"
                no-data-text="Keine Suchergebnisse"
                @update:options="onOptionsChange"
                @toggle-select-all="onSelectAll"
                :server-items-length="appointmentsCount"
                :loading="isLoading"
                loading-text="Wird geladen..."
                show-expand
                :expanded.sync="expanded"
                item-key="group_id"
                hide-default-footer
            >
                <template v-slot:item="{ item, expand, isExpanded, }">
                    <TableRow
                        :appointment="item"
                        :key="item.group_id"
                        :isSelected="selected.some(apt => apt._id === item._id)"
                        :isExpanded="isExpanded"
                        :isPharmacy="isPharmacy"
                        :wfState="tab"
                        :appointmentsInsideGroup="expandedTableRows[item.group_id]"
                        @select="isSelected => select(isSelected, item)"
                        @select-group="isSelected => selectGroup(isSelected, item, ...expandedTableRows[item.group_id])"
                        @expand="expand"
                        @update-by-id="data => updateById(item.group_id, data)"
                        @update-appointments="updateAppointments"
                        @delete="payload => deleteAppointment(item.pharmacy.id, item._id, payload)"
                        @fetch-appointments="refreshAppointmentsAndCounts"
                    />
                </template>
                <template v-slot:expanded-item="{ item }">
                    <TableRow
                        v-for="appointment in expandedTableRows[item.group_id]"
                        :appointment="appointment"
                        :isSelected="selected.some(apt => apt._id === appointment._id)"
                        :isExpanded="expanded.some(apt => apt._id === item._id)"
                        :isPharmacy="isPharmacy"
                        :wfState="tab"
                        @select="isSelected => select(isSelected, appointment)"
                        @delete="payload => deleteAppointment(item.pharmacy.id, appointment._id, payload)"
                        @update-by-id="data => updateById(item.group_id, data)"
                        @fetch-appointments="refreshAppointmentsAndCounts"
                    />
                </template>
            </v-data-table>
            <v-card-text class="footer text-center pt-2">
                <BatchUpdateAppointments
                    v-if="selected.length"
                    :selectedCount="selected.length"
                    :tab="tab"
                    :pharmacy_id="pharmacy_id"
                    @grouping="isGroupDialogOpen = true"
                    @confirm="confirm"
                    @send-to-apobilanz="onMoveToApobilanz"
                    @batch-update="batchUpdateAppointments"
                    @batch-remove="batchDeleteAppointments"
                    @fetch-appointments="fetchAppointments"
                    @confirm-nonbinding="batchConfirmNonBindingAppointments"
                    :isUngroupingEnabled="isUngroupingEnabled"
                    :selectedAppointmentsIds="selectedAppointmentsIds"
                    :selectedAppointmentsPriceOverrides="selectedAppointmentsPriceOverrides"
                    :canConfirmNonbinding="canConfirmNonbinding"
                />

                <v-row class="pagination-footer">
                    <v-col class="d-flex align-center justify-center">
                        Zeilen pro Seite:
                        <v-select
                            v-model="tableOptions.itemsPerPage"
                            :items="[5, 10, 25, 50, 100]"
                            placeholder="Zeilen pro Seite"
                            class="pt-0 ml-2 mt-0 mr-5"
                            hide-details
                        ></v-select>
                        <v-pagination
                            v-model="tableOptions.page"
                            :length="Math.ceil(appointmentsCount / tableOptions.itemsPerPage)"
                            :total-visible="11"
                        ></v-pagination>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
        <AddToGroup
            v-if="isGroupDialogOpen"
            @add-group="addToGroup"
            v-model="isGroupDialogOpen"
            :groupAppointments="groupAppointments"
        />
        <ConfirmationDialog
            v-model="isConfirmDialogOpen"
            :message="confirmationMessage"
            @save="confirmationType === 'ungroup' ? removeFromGroup() : rebuildGroupAndMoveToApobilanz()"
        >
            <ul v-if="confirmationType === 'regroup'" class="pt-0 pb-0">
                <li
                    v-for="appointment in selected"
                    :key="appointment._id"
                    class="font-weight-bold subtitle-1 mt-2"
                    dense
                >
                    <span>{{ appointment.date.split('-').reverse().join('.') }}</span>
                    <span class="ml-4">{{ appointment.display_time }}</span>
                </li>
            </ul>
        </ConfirmationDialog>
    </div>
</template>

<script>
    import axios from 'axios';
    import _ from 'lodash';
    import TableRow from './TableRow.vue';
    import { mapMutations } from 'vuex';
    import AddToGroup from './AddToGroup.vue';
    import ConfirmationDialog from './ConfirmationDialog.vue';
    import BatchUpdateAppointments from './BatchUpdateAppointments.vue';

    export default {
        components: {
            TableRow,
            AddToGroup,
            ConfirmationDialog,
            BatchUpdateAppointments
        },
        props: {
            tab: String,
            tableRowsCount: Number,
            totalAppointmentsCount: Number, // total count including group appointments
            sort: Object,
            filters: Object,
            pharmacy_id: String
        },
        data: () => ({
            search: '',
            pageNumber: 1,
            expanded: [],
            tableOptions: {},
            selected: [],
            headers: [
                { text: '', value: 'data-table-expand', class: 'header-cell' },
                { text: 'Datum', value: 'date', class: 'header-cell' },
                { text: 'bis Datum', value: 'group_enddate', class: 'header-cell' },
                { text: 'Tage', value: 'group_size', class: 'header-cell' },
                { text: 'Apotheke', value: 'pharmacy', class: 'header-cell' },
                { text: 'Zeit', value: 'time', class: 'header-cell' },
                { text: 'Freiberufler', value: 'pharmacist', class: 'header-cell' },
                { text: 'Anfahrt', value: 'travelcosts', class: 'header-cell' },
                { text: 'Betrag', value: '', class: 'header-cell' },
                { text: '', value: 'buttons', class: 'header-cell' },
            ],
            appointments: [],
            appointmentsCount: 0,
            isLoading: false,
            isGroupDialogOpen: false,
            isConfirmDialogOpen: false,
            confirmationType: null,
        }),
        computed: {
            normalizedAppointments() {
                if (!Array.isArray(this.appointments)) return [];
                return this.appointments
                    .filter(group => group && typeof group === 'object')
                    .map(group => ({
                        ...group,
                        appointments: Array.isArray(group.appointments)
                            ? group.appointments.filter(Boolean)
                            : []
                    }))
                    .filter(group => group.appointments.length > 0);
            },
            // returns an array of appointments objects (singles and group-firsts)
            // that represent the main rows of the table
            // each appointment contains its group props
            tableRows() {
                return this.normalizedAppointments.map(({ appointments, ...groupProps }) => ({
                    ...appointments[0],
                    ...groupProps
                }));
            },
            // returns an object containing the appointments inside each group
            // with group_id as key and an array of appointments objects as value
            // each appointment contains its group props
            expandedTableRows() {
                const appointmentsInsideGroups = {};
                this.groupAppointments.forEach(({ appointments, group_id, ...groupProps }) => {
                    appointmentsInsideGroups[group_id] = [
                        ...appointments.slice(1).map(appointment => ({
                            group_id,
                            ...appointment,
                            ...groupProps
                        }))
                    ]
                });
                return appointmentsInsideGroups
            },
            isFilterChanged() {
                return Object.values(this.filters).some(filter => filter != null);
            },
            isPharmacy() {
                return Boolean(this.pharmacy_id)
            },
            selectedAppointmentsIds() {
                return this.selected.map(appointment => appointment._id)
            },
            canConfirmNonbinding() {
                return this.selected.every(appointment => appointment.nonbinding)
            },
            selectedAppointmentsPriceOverrides() {
                return this.selected.map(appointment => appointment.pricing_override)
            },
            isUngroupingEnabled() {
                return this.selected.every(appointment => appointment.group_size > 1)
            },
            groupAppointments() {
                return this.normalizedAppointments.filter(group => (
                    group.group_size > 1
                ))
            },
            confirmationMessage() {
                if (this.confirmationType === 'ungroup') {
                    return `${this.selectedAppointmentsIds.length} Termine aus der Gruppe entfernen?`
                } else if (this.confirmationType === 'regroup') {
                    return `Diese Termine gehören zu einer größeren Gruppe. Diese Termine loslösen und neu gruppieren?`
                }
                return '';
            },
            selectedFulfilledDates() {
                return this.selected.filter(appointment => {
                    return moment(appointment.date, 'YYYY-MM-DD').isBefore(moment(), 'day');
                });
            }
        },
        methods: {
            normalizeGroups(groups) {
                if (!Array.isArray(groups)) return [];
                return groups
                    .filter(group => group && typeof group === 'object')
                    .map(group => ({
                        ...group,
                        appointments: Array.isArray(group.appointments)
                            ? group.appointments.filter(Boolean)
                            : []
                    }))
                    .filter(group => group.appointments.length > 0);
            },
            async onOptionsChange({ page, itemsPerPage, sortBy, sortDesc }) {
                if (this.isFilterChanged) this.fetchFilteredAppointments(page, itemsPerPage);
                else if (this.search) this.searchAppointments(page, itemsPerPage)
                else this.fetchAppointments(page, itemsPerPage, sortBy[0], sortDesc[0] ? -1 : 1);

                if(!this.isPharmacy) this.$nextTick(() => {
                    window.scrollTo(0, 0);
                })
            },

            refreshAppointments() {
                const {
                    page = 1,
                    itemsPerPage = 25,
                    sortBy = [],
                    sortDesc = [],
                } = this.tableOptions;

                if (this.isFilterChanged) {
                    return this.fetchFilteredAppointments(
                        page,
                        itemsPerPage,
                        this.sort?.sortBy,
                        this.sort?.sortAsc,
                    );
                }
                if (this.search) return this.searchAppointments(page, itemsPerPage);

                const selectedSortBy = this.sort?.sortBy || sortBy[0];
                const selectedSortAsc = this.sort?.sortBy
                    ? this.sort.sortAsc
                    : (sortDesc[0] ? -1 : 1);
                return this.fetchAppointments(page, itemsPerPage, selectedSortBy, selectedSortAsc);
            },

            refreshAppointmentsAndCounts() {
                this.refreshAppointments();
                this.$emit('get-counts');
            },

            async fetchAppointments(page = 1, pageSize = 25, sort_by, sort_asc = 1) {
                const sortAsc = this.tab === "paid" && !sort_by ? -1 : sort_asc; // set the sortAsc based on sort_by parameter (which doesn't have a default value now)
                const sortBy = sort_by || 'date'; // as we removed the default value of the parameter 'sort_by', we have to set it here
                try {
                    this.isLoading = true;
                    const response = await axios.get('/api/appointments/overview', {
                        params: {
                            page,
                            page_size: pageSize,
                            sort_by: sortBy,
                            sort_asc: sortAsc,
                            status: this.tab,
                            pharmacy_id: this.pharmacy_id
                        },
                    });
                    this.appointments = this.normalizeGroups(response.data.groups);
                    this.appointmentsCount = this.tableRowsCount;
                    this.tableOptions.page = page;
                    this.tableOptions.itemsPerPage = pageSize;
                } catch (error) {
                    this.alertError();
                } finally {
                    this.isLoading = false;
                }
            },

            searchAppointments: _.debounce(async function (page = 1, pageSize = 25) {
                if (!this.search) return;
                try {
                    this.isLoading = true;
                    const response = await axios.get(`/api/appointments/searchoverview`, {
                        params: {
                            query: this.search,
                            page: page - 1, // solr pagination is zero-indexed
                            page_size: pageSize,
                            wf_state: this.tab == 'completed_no_apo_invoice' ? 'allocated' : this.tab,
                        },
                    });
                    if (response.data.status == 500 || response.data.error) {
                        throw new Error(response.data.error || 'Ungültige Suchanfrage');
                    }

                    const groups = this.normalizeGroups(response.data.results);
                    this.appointments = groups;
                    this.appointmentsCount = response.data.total_results ?? groups.length;
                    this.tableOptions.page = page;
                    this.tableOptions.itemsPerPage = pageSize;
                } catch (error) {
                    this.alertError();
                } finally {
                    this.isLoading = false;
                }
            }, 200),

            async fetchFilteredAppointments(page = 1, pageSize = 25, sort_by, sort_asc) {
                const sortBy = sort_by || this.sort?.sortBy || 'date';
                const sortAsc = sort_asc ?? this.sort?.sortAsc ?? 1;
                const params = {
                    page,
                    page_size: pageSize,
                    wf_state: this.tab,
                    sort_by: sortBy,
                    sort_asc: sortAsc,
                    ...this.filters
                };
                try {
                    this.isLoading = true;
                    const response = await axios.get(`/api/appointments/filtered`, { params });
                    this.appointments = this.normalizeGroups(response.data.groups);
                    this.appointmentsCount = response.data.total_count;
                    this.tableOptions.page = page;
                    this.tableOptions.itemsPerPage = pageSize;
                } catch (error) {
                    this.alertError();
                } finally {
                    this.isLoading = false;
                }
            },

            confirm(type) {
                this.confirmationType = type;
                this.isConfirmDialogOpen = true;
            },

            onMoveToApobilanz() {
                if (this.isUngroupingEnabled && this.selectedFulfilledDates.length) {
                    this.confirm('regroup');
                    this.selected = this.selectedFulfilledDates; // reset selected appointments to only fulfilled ones
                } else {
                    this.batchUpdateAppointments('send_to_apobilanz');
                }
            },

            async rebuildGroupAndMoveToApobilanz() {
                await this.removeFromGroup(false); // remove from current group without resetting selected appointments
                if (this.selected.length > 1) await this.addToGroup({ mode: 0 }, false); // create new group with selected appointments
                await this.batchUpdateAppointments('send_to_apobilanz'); // move new group to apobilanz
            },

            async batchUpdateAppointments(path) {
                try {
                    const response = await axios.post(`/pharmacies/${this.pharmacy_id}/${path}`, {
                        'aids': this.selectedAppointmentsIds
                    });
                    this.selected = [];
                    this.alertSuccess(response.data.message);
                    this.$emit('get-counts');
                } catch (error) {
                    this.alertError();
                }
            },

            async batchDeleteAppointments(payload) {
                try {
                    const response = await axios.post(`/pharmacies/${this.pharmacy_id}/batch_remove`, {
                        'aids': this.selectedAppointmentsIds,
                        ...payload
                    });
                    this.selected = [];
                    this.alertSuccess(response.data.message);
                    this.$emit('get-counts');
                } catch (error) {
                    this.alertError();
                }
            },

            async batchConfirmNonBindingAppointments() {
                try {
                    this.isLoading = true;
                    const response = await axios.post(`/api/appointments/${this.pharmacy_id}/confirm_none_binidng_appointment`, {
                        'appointment_ids': this.selectedAppointmentsIds
                    });
                    this.fetchAppointments();
                    this.selected = [];
                    this.alertSuccess();
                } catch (error) {
                    this.alertError();
                } finally {
                    this.isLoading = false
                }
            },

            async deleteAppointment(pharmacyId, appointmentId, payload) {
                try {
                    const response = await axios.post(`/pharmacies/${pharmacyId}/${appointmentId}/remove`, payload);
                    this.alertSuccess(response.data.message);
                    this.$emit('get-counts');
                } catch (error) {
                    this.alertError()
                }
            },

            select(isSelected, appointment) {
                isSelected
                    ? this.selected.push(appointment)
                    : this.selected = this.selected.filter(apt => apt._id !== appointment._id)
            },
            selectGroup(isSelected, ...group) {
                group.forEach(apt => this.select(isSelected, apt))
            },
            /**
             * fires when the select-all checkbox is clicked
             * @param value: a Boolean that determines whether the checkbox is checked or not
             */
            onSelectAll({ value }) {
                this.selected = []; // reset selected and expanded rows
                this.expanded = [];
                if (!value) return // return if it's false (unchecked)

                // set the expanded and selected rows manually
                this.expanded = this.normalizedAppointments.filter(group => (
                    group.group_size > 1
                ))
                this.$nextTick(() => {
                    this.normalizedAppointments.forEach(group => {
                        group.group_size > 1
                            ? this.selectGroup(true, ...group.appointments)
                            : this.select(true, group.appointments[0])
                    })
                })
            },
            updateById(groupId, {_id, ...props}) {
                const group = this.normalizedAppointments.find(gp => gp.group_id === groupId)
                if (!group) return;
                let appointment = group.appointments.find(appointment => appointment._id === _id);
                if (!appointment) return;

                Object.entries(props).forEach(([key, value]) => {
                    this.$set(appointment, key, value)
                })
            },
            updateAppointments(appointment) {
                const indexOfAppointment = this.appointments.findIndex(appt => appt.group_id === appointment.group_id);
                this.appointments.splice(indexOfAppointment, 1, appointment);
            },
            async addToGroup({ mode=0, groupId }, resetSelected = true) {
                try {
                    const response = await axios.post(`/pharmacies/${this.pharmacy_id}/add_appointment_to_group`, {
                        add_mode: mode,
                        appointment_ids: this.selectedAppointmentsIds,
                        group_id: groupId
                    });
                    if (response.data.status == 400) throw new Error(response.data.message)
                    if (resetSelected) {
                        await this.fetchAppointments();
                        await this.$nextTick();
                        this.selected = [];
                    }
                    this.alertSuccess('Gruppe wurde erstellt.')

                } catch (error) {
                    this.alertError(error)
                }
            },
            async removeFromGroup(resetSelected = true) {
                try {
                    const response = await axios.post(`/pharmacies/${this.pharmacy_id}/remove_appointment_from_group`, {
                        appointment_ids: this.selectedAppointmentsIds,
                    });
                    if (response.data.status === 400) throw new Error(response.data.message)
                    if (resetSelected) {
                        await this.fetchAppointments();
                        await this.$nextTick();
                        this.selected = [];
                    }
                    this.alertSuccess('Termin(e) erfolgreich aus Gruppe entfernt.')

                } catch (error) {
                    this.alertError(error)
                }
            },

            async batchOverridePricing(payload) {
                try {
                    const response = await axios.post(`/pharmacies/${this.pharmacy_id}/batch_remove`, {
                        'aids': this.selectedAppointmentsIds,
                        ...payload
                    });
                    this.selected = [];
                    this.alertSuccess(response.data.message);
                    this.$emit('get-counts');
                } catch (error) {
                    this.alertError();
                }
            },

            ...mapMutations({
                alertError: 'alert/error',
                alertSuccess: 'alert/success',
            })
        },
        watch: {
            search(text) {
                text?.trim().length
                    ? this.searchAppointments()
                    : this.fetchAppointments()
            },
            filters: {
                handler() {
                    this.isFilterChanged
                        ? this.fetchFilteredAppointments()
                        : this.fetchAppointments()
                },
                deep: true
            },
            sort: {
                handler({ sortBy, sortAsc }) {
                    const { page, itemsPerPage } = this.tableOptions;
                    if (this.isFilterChanged) {
                        this.fetchFilteredAppointments(page, itemsPerPage, sortBy, sortAsc);
                    } else if (!sortBy) {
                        this.fetchAppointments(page, itemsPerPage);
                    } else {
                        this.fetchAppointments(page, itemsPerPage, sortBy, sortAsc);
                    }
                },
                deep: true
            },
            totalAppointmentsCount() {
                this.refreshAppointments(); // get appointments if the total count is changed
            }
        },
        created() {
            if (this.isPharmacy) {
                this.headers.splice(0, 0, {
                    text: '',
                    value: 'data-table-select',
                    class: 'header-cell'
                })
            }
        },
    };
</script>

<style>
    .appointments-overview .footer {
        position: -webkit-sticky;
        position: sticky;
        bottom: 0;
        background: #f0fcff;
        border-top: thin solid rgb(0 0 0 / 16%);
        padding-inline: 11px;
    }
    .footer .v-select {
        max-width: 50px;
    }
    .batch-update {
        background-color: #d4fce5;
    }
    .price-actions {
        width: 100%;
    }
    .header-cell {
        font-size: 0.8rem !important;
    }
    .header-cell span {
        display: block;
    }
    .mdi-checkbox-marked {
        color: #1976d2 !important;
        caret-color: #1976d2 !important;
    }
</style>
